import React, { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button, Col, Container, DropdownItem, DropdownMenu, DropdownToggle,
  Form, FormGroup, Input, Label, Media, Row, UncontrolledDropdown,
} from 'reactstrap';
import { MoreVertical, PlusCircle } from 'react-feather';
import { FaPen, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { H4, Spinner } from '../../../../../AbstractElements';
import CommonModal from '../../../../UiKits/Modals/common/modal';
import { baseURL } from '../../../../../Services/api/baseURL';
import Loader from '../../../../Loader/Loader';

const selectStyles = {
  control: (base) => ({ ...base, borderRadius: '8px', minHeight: '38px' }),
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'link',
];

const initialForm = {
  question: '',
  answer: '',
  category: '',
  order: 0,
  isActive: true,
};

const FAQManagement = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem('token'));

  const resetForm = () => setForm(initialForm);

  const toggleAddModal = () => {
    setAddModal((p) => !p);
    setEditData(null);
    resetForm();
  };

  const handleField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      const res = await axios.get(
        `${baseURL}/api/content-settings/faq/items?${params.toString()}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${baseURL}/api/content-settings/faq/categories`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setCategories((res.data.data || []).map((c) => ({ label: c.name, value: c.name })));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchFaqs();
    // eslint-disable-next-line
  }, [categoryFilter]);

  const handleCreateCategory = async (inputValue) => {
    const name = inputValue.trim();
    if (!name) return;
    setCreatingCategory(true);
    try {
      await axios.post(
        `${baseURL}/api/content-settings/faq/categories`,
        { name },
        { headers: { Authorization: `${getToken()}` } }
      );
      const option = { label: name, value: name };
      setCategories((prev) => [...prev, option]);
      handleField('category', name);
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: err?.response?.data?.message || 'Failed to create category',
        icon: 'error',
        confirmButtonColor: '#d3178a',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.question || !form.answer) {
      Swal.fire({ title: 'Question and answer are required', icon: 'warning', confirmButtonColor: '#d3178a' });
      return;
    }
    setSaving(true);
    try {
      if (editData) {
        await axios.patch(
          `${baseURL}/api/content-settings/faq/items/${editData._id}`,
          form,
          { headers: { Authorization: `${getToken()}` } }
        );
        Swal.fire({ title: 'FAQ Updated!', icon: 'success', confirmButtonColor: '#d3178a' });
      } else {
        await axios.post(
          `${baseURL}/api/content-settings/faq/items`,
          form,
          { headers: { Authorization: `${getToken()}` } }
        );
        Swal.fire({ title: 'FAQ Added!', icon: 'success', confirmButtonColor: '#d3178a' });
      }
      toggleAddModal();
      fetchFaqs();
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Failed to save FAQ', icon: 'error', confirmButtonColor: '#d3178a' });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditData(row);
    setForm({
      question: row.question || '',
      answer: row.answer || '',
      category: row.category || 'General',
      order: row.order || 0,
      isActive: row.isActive ?? true,
    });
    setAddModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(
        `${baseURL}/api/content-settings/faq/items/${deleteTarget._id}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchFaqs();
      Swal.fire({ title: 'FAQ Deleted', icon: 'success', confirmButtonColor: '#d3178a' });
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = data.filter((item) =>
    `${item.question} ${item.answer} ${item.category}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      name: '#',
      selector: (row) => row.order,
      sortable: true,
      center: true,
      width: '60px',
    },
    {
      name: 'Category',
      selector: (row) => row.category,
      sortable: true,
      center: true,
      width: '150px',
      cell: (row) => (
        <span
          style={{
            background: '#fff0f7',
            color: '#d3178a',
            borderRadius: '12px',
            padding: '2px 10px',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {row.category}
        </span>
      ),
    },
    {
      name: 'Question',
      selector: (row) => row.question,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <span style={{ fontWeight: 500 }}>{row.question}</span>
      ),
    },
    {
      name: 'Answer',
      selector: (row) => row.answer,
      grow: 2,
      cell: (row) => (
        <span
          style={{ color: '#666', fontSize: '13px' }}
          dangerouslySetInnerHTML={{
            __html: row.answer?.length > 100
              ? row.answer.substring(0, 100) + '…'
              : row.answer,
          }}
        />
      ),
    },
    {
      name: 'Status',
      selector: (row) => row.isActive,
      center: true,
      width: '100px',
      cell: (row) => (
        <p className={`mb-0 ${row.isActive ? 'text-success' : 'text-danger'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </p>
      ),
    },
    {
      name: 'Updated',
      selector: (row) => row.updatedAt,
      sortable: true,
      center: true,
      width: '110px',
      cell: (row) => moment(row.updatedAt).format('DD/MM/YYYY'),
    },
    {
      name: 'Actions',
      center: true,
      width: '80px',
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => openEdit(row)}>
              Edit <FaPen size={12} />
            </DropdownItem>
            <DropdownItem
              style={{ color: '#e53e3e' }}
              onClick={() => { setDeleteTarget(row); setDeleteModal(true); }}
            >
              Delete <FaTrash size={12} />
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <Fragment>
      <Container fluid style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm="12">
            <div
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}
            >
              {/* Header */}
              <Row className="pb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <H4>FAQ Management</H4>
                  <div className="file-content">
                    <Media>
                      <div className="mb-0 form-group position-relative search_outer d-flex align-items-center me-2">
                        <i className="fa fa-search" style={{
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                          }}></i>
                        <input
                          className="form-control border-0"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          type="text"
                          placeholder="Search FAQs..."
                        />
                      </div>
                      <div className="me-3" style={{ width: '200px' }}>
                        <Select
                          options={categories}
                          value={categories.find((c) => c.value === categoryFilter) || null}
                          onChange={(option) => setCategoryFilter(option ? option.value : '')}
                          isClearable
                          placeholder="All Categories"
                          styles={selectStyles}
                        />
                      </div>
                      <Media body className="text-end">
                        <Button
                          className="btn btn-primary d-flex align-items-center gap-1"
                          onClick={toggleAddModal}
                          style={{ background: '#d3178a', border: 'none', borderRadius: '8px' }}
                        >
                          <PlusCircle size={16} />
                          Add FAQ
                        </Button>
                      </Media>
                    </Media>
                  </div>
                </div>
              </Row>

              {/* Table */}
              {loading ? (
                <Col xxl="12" className="flex justify-center items-center">
                  <div className="loader-box"><Spinner /></div>
                </Col>
              ) : (
                <DataTable
                  data={filtered}
                  columns={columns}
                  pagination
                  progressPending={loading}
                  progressComponent={<Loader />}
                  noDataComponent={
                    <div style={{ padding: '40px', color: '#999', textAlign: 'center' }}>
                      No FAQs found. Click "Add FAQ" to get started.
                    </div>
                  }
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Add / Edit Modal */}
      <CommonModal
        isOpen={addModal}
        title={editData ? 'Edit FAQ' : 'Add New FAQ'}
        className="store_modal"
        toggler={toggleAddModal}
        size="lg"
      >
        <Container>
          <Form>
            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label className="font-medium text-base">
                    Category
                  </Label>
                  <CreatableSelect
                    options={categories}
                    value={form.category ? { label: form.category, value: form.category } : null}
                    onChange={(option) => handleField('category', option ? option.value : '')}
                    onCreateOption={handleCreateCategory}
                    isDisabled={creatingCategory}
                    isLoading={creatingCategory}
                    placeholder="Select or type to create a category..."
                    styles={selectStyles}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="font-medium text-base">Order</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.order}
                    onChange={(e) => handleField('order', parseInt(e.target.value) || 0)}
                    style={{ borderRadius: '8px' }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label className="font-medium text-base">
                Question <span className="text-danger">*</span>
              </Label>
              <Input
                value={form.question}
                onChange={(e) => handleField('question', e.target.value)}
                placeholder="e.g. What is the minimum order amount?"
                style={{ borderRadius: '8px' }}
              />
            </FormGroup>

            <FormGroup>
              <Label className="font-medium text-base">
                Answer <span className="text-danger">*</span>
              </Label>
              <ReactQuill
                theme="snow"
                value={form.answer}
                onChange={(val) => handleField('answer', val)}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write the answer here..."
                style={{ height: '200px', marginBottom: '50px' }}
              />
            </FormGroup>

            <FormGroup>
              <label className="d-flex align-items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => handleField('isActive', e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                  style={{ accentColor: '#d3178a' }}
                />
                <span>Active (visible on site)</span>
              </label>
            </FormGroup>

            <Row>
              <Col className="text-center">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  style={{
                    background: '#d3178a',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '10px 40px',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  {saving ? 'Saving…' : editData ? 'Update FAQ' : 'Save FAQ'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Container>
      </CommonModal>

      {/* Delete Confirm */}
      <CommonModal
        isOpen={deleteModal}
        title="Delete FAQ"
        toggler={() => { setDeleteModal(false); setDeleteTarget(null); }}
        size="sm"
      >
        <Container className="d-flex flex-column align-items-center py-2">
          <p style={{ textAlign: 'center', color: '#555' }}>
            Are you sure you want to delete this FAQ?
          </p>
          <p
            style={{
              fontWeight: 600,
              color: '#333',
              textAlign: 'center',
              fontSize: '14px',
            }}
          >
            "{deleteTarget?.question}"
          </p>
          <div className="d-flex gap-3 mt-2">
            <div
              onClick={() => { setDeleteModal(false); setDeleteTarget(null); }}
              style={{
                border: '1px solid #ccc',
                padding: '8px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                color: '#555',
              }}
            >
              Cancel
            </div>
            <div
              onClick={confirmDelete}
              style={{
                background: '#e53e3e',
                padding: '8px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              Delete
            </div>
          </div>
        </Container>
      </CommonModal>
    </Fragment>
  );
};

export default FAQManagement;
